import React, { useState, useEffect } from 'react'

import {
  Typography,
  Box,
  Button,
  CircularProgress,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Card,
  CardContent,
} from '@mui/material'
import { Add, Delete, Edit } from '@mui/icons-material'
import useDropdowns from '../../hooks/api/useDropdowns'
import useIsAdmin from '../../hooks/useIsAdmin'
import GenericLoadError from '../../common/GenericLoadError'
import EventModal from './EventModal'
import useAlert from '../../hooks/useAlert'
import {
  removeItemFromArray,
  updateObjectWithMatchingId,
} from '../../utils/array'
import { addTimezoneOffsetToTimestamp } from '../../utils/date'
import { authedAxios } from '../../utils'
import { useNavigate } from 'react-router-dom'

const Events = () => {
  const [events, setEvents] = useState([])
  const [selectedEvent, setSelectedEvent] = useState(null)
  const [modalOpen, setModalOpen] = useState(false)

  const [loading, setLoading] = useState(false)
  const [errorLoading, setErrorLoading] = useState(false)

  const { dropdownsSummary, ..._dropdowns } = useDropdowns()

  useIsAdmin

  const getEvents = () => {
    setLoading(true)
    authedAxios
      .get(`/api/event?date=${new Date().toLocaleDateString('sv')}`)
      .then((res) => {
        setLoading(false)
        setEvents(res.data.events)
      })
      .catch((_e) => {
        setErrorLoading(true)
      })
      .finally(() => {
        setLoading(false)
      })
  }

  useEffect(() => {
    getEvents()
  }, [])

  const { createAlert, createGenericErrorAlert } = useAlert()

  const saveEvent = (newVal) => {
    if (selectedEvent?.id) {
      return authedAxios
        .put(`/api/event/${newVal.id}`, newVal)
        .then((res) => {
          setSelectedEvent(null)
          setModalOpen(false)
          setEvents(updateObjectWithMatchingId(events, res.data.event))
          createAlert({ message: 'Successfully saved event' })
        })
        .catch(() => {
          createGenericErrorAlert()
        })
    }

    return authedAxios
      .post('/api/event', newVal)
      .then((res) => {
        setSelectedEvent(null)
        setModalOpen(false)
        setEvents([res.data.event, ...events])
        createAlert({ message: 'Successfully saved event' })
      })
      .catch(() => {
        createGenericErrorAlert()
      })
  }

  const archiveEvent = (event) =>
    authedAxios
      .delete(`/api/event/${event.id}`)
      .then(() => {
        setEvents(removeItemFromArray(events, event))
      })
      .catch(() => {
        createGenericErrorAlert()
      })

  const hasLoadError = _dropdowns.hasLoadError || errorLoading
  const isLoading = _dropdowns.isLoading || loading

  const { isAdmin } = useIsAdmin()
  const navigate = useNavigate()

  if (isLoading) return <CircularProgress />
  if (hasLoadError) return <GenericLoadError />

  const eventTypes =
    dropdownsSummary.find((d) => d.key === 'eventType')?.options || []

  if (eventTypes?.length === 0)
    return (
      <GenericLoadError
        color='warning'
        action={
          isAdmin && (
            <Button
              onClick={() => navigate('/settings/dropdowns?tab=eventType')}
              size='small'
            >
              Click here to set them up
            </Button>
          )
        }
      >
        Event types are not configured.
      </GenericLoadError>
    )

  return (
    <Box sx={{ width: '100%', mt: 2 }}>
      {eventTypes?.length > 0 && (
        <EventModal
          eventTypes={eventTypes}
          event={selectedEvent}
          isOpen={modalOpen}
          onClose={() => {
            setSelectedEvent(null)
            setModalOpen(false)
          }}
          onSave={saveEvent}
        />
      )}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant='h6'>Events</Typography>
            <Button
              onClick={() => {
                setSelectedEvent(null)
                setModalOpen(true)
              }}
              endIcon={<Add />}
            >
              Create an event
            </Button>
          </Box>

          {events.length === 0 && (
            <Box>
              <i>No events </i>
            </Box>
          )}

          {events.length > 0 && (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Event Type</TableCell>
                  <TableCell>Event Name</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {events.map((e) => {
                  const dateString = addTimezoneOffsetToTimestamp(
                    e.date
                  ).toLocaleDateString()

                  const typeString = eventTypes.find(
                    (et) => et.id === e.typeId
                  )?.name
                  return (
                    <TableRow key={e.id}>
                      <TableCell>{dateString}</TableCell>
                      <TableCell>{typeString}</TableCell>
                      <TableCell>{e.name}</TableCell>
                      <TableCell>{e.description}</TableCell>
                      <TableCell>
                        <IconButton
                          onClick={() => {
                            setSelectedEvent(e)
                            setModalOpen(true)
                          }}
                        >
                          <Edit />
                        </IconButton>
                        <IconButton
                          onClick={() => {
                            archiveEvent(e)
                          }}
                        >
                          <Delete />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </Box>
  )
}

export default Events
